import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  ConfirmDialog,
  Drawer,
  Empty,
  ErrorState,
  Loading,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../components/ui';
import { api, fieldErrors } from '../lib/api';
import { money } from '../lib/format';

/* ------------------------------------------------------------------ */
/* Billing periods for one plan                                         */
/* ------------------------------------------------------------------ */

export interface BillingOption {
  id: string;
  planId: string;
  intervalUnit: 'MONTH' | 'YEAR';
  intervalCount: number;
  label: string;
  price: number;
  currency: string;
  supportsOneTime: boolean;
  supportsRecurring: boolean;
  cashfreePlanId: string | null;
  isActive: boolean;
  sortOrder: number;
  subscribers: number;
}

/**
 * The periods offered up front, so the common ones are a pick rather than
 * two fields. Anything else is still expressible by editing the count.
 */
const PRESETS = [
  { label: 'Monthly', intervalUnit: 'MONTH' as const, intervalCount: 1 },
  { label: 'Quarterly', intervalUnit: 'MONTH' as const, intervalCount: 3 },
  { label: 'Half-yearly', intervalUnit: 'MONTH' as const, intervalCount: 6 },
  { label: 'Yearly', intervalUnit: 'YEAR' as const, intervalCount: 1 },
];

const BLANK = {
  id: '',
  intervalUnit: 'MONTH' as 'MONTH' | 'YEAR',
  intervalCount: 1,
  price: 0,
  currency: 'INR',
  supportsOneTime: true,
  supportsRecurring: false,
  cashfreePlanId: '',
  isActive: true,
  sortOrder: 0,
};

export function BillingOptionsDrawer({
  planId,
  planName,
  onClose,
}: {
  planId: string;
  planName: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [editing, setEditing] = useState<typeof BLANK | null>(null);
  const [removing, setRemoving] = useState<BillingOption | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['platform', 'billing-options', planId],
    queryFn: () => api.get<BillingOption[]>(`/api/platform/plans/${planId}/billing-options`),
  });

  const refresh = () =>
    client.invalidateQueries({ queryKey: ['platform', 'billing-options', planId] });

  const save = useMutation({
    mutationFn: (row: typeof BLANK) => {
      const payload = {
        intervalUnit: row.intervalUnit,
        intervalCount: Number(row.intervalCount),
        price: Number(row.price),
        currency: row.currency,
        supportsOneTime: row.supportsOneTime,
        supportsRecurring: row.supportsRecurring,
        cashfreePlanId: row.cashfreePlanId,
        isActive: row.isActive,
        sortOrder: Number(row.sortOrder),
      };
      return row.id
        ? api.patch(`/api/platform/billing-options/${row.id}`, payload)
        : api.post(`/api/platform/plans/${planId}/billing-options`, payload);
    },
    onSuccess: (_d, row) => {
      push(row.id ? 'Period updated' : 'Period added', 'ok');
      setEditing(null);
      setErrors({});
      void refresh();
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const remove = useMutation({
    mutationFn: (row: BillingOption) => api.delete(`/api/platform/billing-options/${row.id}`),
    onSuccess: () => {
      push('Period removed', 'ok');
      setRemoving(null);
      void refresh();
    },
    onError: (caught) => {
      setRemoving(null);
      fail(caught);
    },
  });

  const rows = query.data ?? [];
  const usedPresets = new Set(rows.map((r) => `${r.intervalUnit}:${r.intervalCount}`));

  return (
    <Drawer title={`${planName} — billing periods`} onClose={onClose} size="wide">
      <div className="col gap-16">
        <div className="small muted">
          The plan carries the features and limits; each period below carries a price. The same
          entitlement is shared by all of them, so there is no need for a separate plan per period.
        </div>

        <div className="row gap-6 wrap">
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="btn sm"
              disabled={usedPresets.has(`${preset.intervalUnit}:${preset.intervalCount}`)}
              title={
                usedPresets.has(`${preset.intervalUnit}:${preset.intervalCount}`)
                  ? 'This plan already has that period'
                  : undefined
              }
              onClick={() =>
                setEditing({
                  ...BLANK,
                  intervalUnit: preset.intervalUnit,
                  intervalCount: preset.intervalCount,
                  sortOrder: rows.length,
                })
              }
            >
              + {preset.label}
            </button>
          ))}
        </div>

        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          <Empty message="No periods yet — nobody can buy this plan until one exists." />
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="num">Price</th>
                  <th>Purchasable as</th>
                  <th>Cashfree plan</th>
                  <th className="num" title="Hotels currently on this period">
                    Subscribers
                  </th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="b">{row.label}</td>
                    <td className="num b">{money(row.price)}</td>
                    <td className="small">
                      {row.supportsOneTime ? <span className="chip">One-time</span> : null}
                      {row.supportsRecurring ? (
                        <span className="chip info" style={{ marginLeft: 4 }}>
                          Auto-renew
                        </span>
                      ) : null}
                    </td>
                    <td className="xs mono muted">
                      {row.cashfreePlanId ?? (row.supportsRecurring ? '—' : 'not needed')}
                    </td>
                    <td className="num">{row.subscribers}</td>
                    <td>
                      <span className={`chip ${row.isActive ? 'ok' : 'danger'}`}>
                        {row.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="right">
                      <button
                        className="btn ghost sm"
                        onClick={() =>
                          setEditing({ ...row, cashfreePlanId: row.cashfreePlanId ?? '' })
                        }
                      >
                        Edit
                      </button>
                      <button
                        className="btn ghost sm danger"
                        disabled={row.subscribers > 0}
                        title={
                          row.subscribers > 0
                            ? 'Hotels are on this period — switch it to inactive instead'
                            : undefined
                        }
                        onClick={() => setRemoving(row)}
                        style={{ marginLeft: 4 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <Modal
          title={editing.id ? 'Edit period' : 'New period'}
          onClose={() => {
            setEditing(null);
            setErrors({});
          }}
          footer={
            <>
              <button
                className="btn"
                onClick={() => {
                  setEditing(null);
                  setErrors({});
                }}
              >
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={save.isPending}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending ? 'Saving…' : editing.id ? 'Save period' : 'Add period'}
              </button>
            </>
          }
        >
          <div className="form-grid">
            <Select
              label="Unit"
              value={editing.intervalUnit}
              error={errors.intervalUnit}
              disabled={
                Boolean(editing.id && editing.price >= 0 && editing.sortOrder >= 0) && false
              }
              onChange={(e) =>
                setEditing({ ...editing, intervalUnit: e.target.value as 'MONTH' | 'YEAR' })
              }
            >
              <option value="MONTH">Months</option>
              <option value="YEAR">Years</option>
            </Select>
            <TextInput
              label="How many"
              type="number"
              min={1}
              max={12}
              hint="3 months is a quarter, 6 a half year."
              error={errors.intervalCount}
              value={editing.intervalCount}
              onChange={(e) => setEditing({ ...editing, intervalCount: Number(e.target.value) })}
            />
            <TextInput
              label="Price for the period (₹)"
              type="number"
              min={0}
              hint="Before tax. Tax is added from the billing settings."
              error={errors.price}
              value={editing.price}
              onChange={(e) => setEditing({ ...editing, price: Number(e.target.value) })}
            />
            <TextInput
              label="Order"
              type="number"
              min={0}
              error={errors.sortOrder}
              value={editing.sortOrder}
              onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
            />
            <div className="span2">
              <TextInput
                label="Cashfree plan ID"
                hint="Required before auto-renew can be offered for this period."
                placeholder="sarthi_silver_monthly"
                error={errors.cashfreePlanId}
                value={editing.cashfreePlanId}
                onChange={(e) => setEditing({ ...editing, cashfreePlanId: e.target.value })}
              />
            </div>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.supportsOneTime}
                onChange={(e) => setEditing({ ...editing, supportsOneTime: e.target.checked })}
              />
              Can be paid once
            </label>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.supportsRecurring}
                disabled={!editing.cashfreePlanId.trim()}
                title={editing.cashfreePlanId.trim() ? undefined : 'Map a Cashfree plan first'}
                onChange={(e) => setEditing({ ...editing, supportsRecurring: e.target.checked })}
              />
              Can auto-renew
            </label>
            <label className="row gap-6 span2">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Offered to customers
            </label>
          </div>
        </Modal>
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={`Remove the ${removing.label.toLowerCase()} period?`}
          message="Nobody is on it, so nothing billed changes. To take it off sale while keeping its history, switch it to inactive instead."
          confirmLabel="Remove"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Billing settings                                                     */
/* ------------------------------------------------------------------ */

interface BillingEntity {
  legalName: string;
  gstin: string;
  address: string;
  stateCode: string;
  email: string;
  phone: string;
  taxRate: number;
  taxLabel: string;
  sacCode: string;
  invoicePrefix: string;
  currency: string;
}

interface CashfreeView {
  environment: 'TEST' | 'LIVE';
  appId: string;
  isEnabled: boolean;
  hasSecretKey: boolean;
  hasWebhookSecret: boolean;
  baseUrl: string;
}

export function PlatformBillingSettingsPage() {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [entity, setEntity] = useState<BillingEntity | null>(null);
  const [cashfree, setCashfree] = useState<
    (CashfreeView & { secretKey: string; webhookSecret: string }) | null
  >(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const entityQuery = useQuery({
    queryKey: ['platform', 'billing-entity'],
    queryFn: () => api.get<BillingEntity>('/api/platform/settings/billing-entity'),
  });
  const cashfreeQuery = useQuery({
    queryKey: ['platform', 'cashfree'],
    queryFn: () => api.get<CashfreeView>('/api/platform/settings/cashfree'),
  });

  const saveEntity = useMutation({
    mutationFn: (value: BillingEntity) =>
      api.put('/api/platform/settings/billing-entity', {
        ...value,
        taxRate: Number(value.taxRate),
      }),
    onSuccess: () => {
      push('Billing entity saved', 'ok');
      setEntity(null);
      void client.invalidateQueries({ queryKey: ['platform', 'billing-entity'] });
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const saveCashfree = useMutation({
    mutationFn: (value: CashfreeView & { secretKey: string; webhookSecret: string }) =>
      api.put('/api/platform/settings/cashfree', {
        environment: value.environment,
        appId: value.appId,
        // Blank means "keep what is stored": the screen is never sent the
        // secret, so it cannot send one back.
        secretKey: value.secretKey,
        webhookSecret: value.webhookSecret,
        isEnabled: value.isEnabled,
      }),
    onSuccess: () => {
      push('Cashfree settings saved', 'ok');
      setCashfree(null);
      void client.invalidateQueries({ queryKey: ['platform', 'cashfree'] });
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  if (entityQuery.isLoading || cashfreeQuery.isLoading) return <Loading />;

  const savedEntity = entityQuery.data!;
  const savedCashfree = cashfreeQuery.data!;
  const form = entity ?? savedEntity;
  const cf = cashfree ?? { ...savedCashfree, secretKey: '', webhookSecret: '' };
  const entityIncomplete = !savedEntity.legalName.trim();

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>Billing entity</h2>
            <div className="small muted">
              Who issues a subscription invoice, and at what tax rate. Snapshotted onto each
              invoice, so changing this never restates one already issued.
            </div>
          </div>
          {entityIncomplete ? <span className="chip danger">Not configured</span> : null}
        </div>
        <div className="card-body col gap-16">
          {entityIncomplete ? (
            <div className="form-note">
              No subscription can be charged until this is filled in — an invoice needs somebody to
              issue it.
            </div>
          ) : null}
          <div className="form-grid">
            <div className="span2">
              <TextInput
                label="Legal name *"
                placeholder="Europium Solutions Pvt Ltd"
                error={errors.legalName}
                value={form.legalName}
                onChange={(e) => setEntity({ ...form, legalName: e.target.value })}
              />
            </div>
            <TextInput
              label="GSTIN"
              placeholder="29AABCE1234M1Z5"
              error={errors.gstin}
              value={form.gstin}
              onChange={(e) => setEntity({ ...form, gstin: e.target.value.toUpperCase() })}
            />
            <TextInput
              label="State code"
              maxLength={2}
              hint="Two digits, for place of supply."
              error={errors.stateCode}
              value={form.stateCode}
              onChange={(e) => setEntity({ ...form, stateCode: e.target.value })}
            />
            <div className="span2">
              <TextInput
                label="Address"
                error={errors.address}
                value={form.address}
                onChange={(e) => setEntity({ ...form, address: e.target.value })}
              />
            </div>
            <TextInput
              label="Billing email"
              type="email"
              error={errors.email}
              value={form.email}
              onChange={(e) => setEntity({ ...form, email: e.target.value })}
            />
            <TextInput
              label="Phone"
              error={errors.phone}
              value={form.phone}
              onChange={(e) => setEntity({ ...form, phone: e.target.value })}
            />
            <TextInput
              label="Tax rate (%)"
              type="number"
              min={0}
              max={100}
              hint="Applied to every subscription charge."
              error={errors.taxRate}
              value={form.taxRate}
              onChange={(e) => setEntity({ ...form, taxRate: Number(e.target.value) })}
            />
            <TextInput
              label="Tax label"
              placeholder="GST"
              error={errors.taxLabel}
              value={form.taxLabel}
              onChange={(e) => setEntity({ ...form, taxLabel: e.target.value })}
            />
            {/*
             * Asked for rather than written into the code: it is a tax
             * classification of what this business sells, and getting it
             * wrong is the seller's problem with their own filing.
             */}
            <TextInput
              label="SAC code"
              placeholder="998314"
              hint="Printed on every invoice. Your accountant will know it."
              value={form.sacCode ?? ''}
              onChange={(e) => setEntity({ ...form, sacCode: e.target.value })}
            />
            <TextInput
              label="Invoice prefix"
              placeholder="EUR/SUB"
              hint="Numbers run as PREFIX/YEAR/000001."
              error={errors.invoicePrefix}
              value={form.invoicePrefix}
              onChange={(e) => setEntity({ ...form, invoicePrefix: e.target.value })}
            />
            <TextInput
              label="Currency"
              maxLength={3}
              error={errors.currency}
              value={form.currency}
              onChange={(e) => setEntity({ ...form, currency: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="row gap-6">
            <button
              className="btn primary"
              disabled={saveEntity.isPending || !entity}
              onClick={() => saveEntity.mutate(form)}
            >
              {saveEntity.isPending ? 'Saving…' : 'Save billing entity'}
            </button>
            {entity ? (
              <button className="btn" onClick={() => setEntity(null)}>
                Discard changes
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Cashfree</h2>
            <div className="small muted">
              Credentials live here rather than in the deployment, so moving from the sandbox to the
              live account does not need a release.
            </div>
          </div>
          <span className={`chip ${savedCashfree.environment === 'LIVE' ? 'danger' : 'info'}`}>
            {savedCashfree.environment}
          </span>
        </div>
        <div className="card-body col gap-16">
          <div className="form-grid">
            <Select
              label="Environment"
              hint="LIVE takes real money."
              value={cf.environment}
              onChange={(e) =>
                setCashfree({ ...cf, environment: e.target.value as 'TEST' | 'LIVE' })
              }
            >
              <option value="TEST">Test (sandbox)</option>
              <option value="LIVE">Live</option>
            </Select>
            <TextInput
              label="App ID (x-client-id)"
              error={errors.appId}
              value={cf.appId}
              onChange={(e) => setCashfree({ ...cf, appId: e.target.value })}
            />
            <TextInput
              label="Secret key (x-client-secret)"
              type="password"
              autoComplete="off"
              placeholder={savedCashfree.hasSecretKey ? '•••••••• saved' : 'Not set'}
              hint="Leave blank to keep the stored key."
              error={errors.secretKey}
              value={cf.secretKey}
              onChange={(e) => setCashfree({ ...cf, secretKey: e.target.value })}
            />
            <TextInput
              label="Webhook secret"
              type="password"
              autoComplete="off"
              placeholder={savedCashfree.hasWebhookSecret ? '•••••••• saved' : 'Not set'}
              hint="Verifies that a webhook really came from Cashfree."
              error={errors.webhookSecret}
              value={cf.webhookSecret}
              onChange={(e) => setCashfree({ ...cf, webhookSecret: e.target.value })}
            />
            <label className="row gap-6 span2">
              <input
                type="checkbox"
                checked={cf.isEnabled}
                onChange={(e) => setCashfree({ ...cf, isEnabled: e.target.checked })}
              />
              Enabled — subscriptions can be charged
            </label>
          </div>
          <div className="small muted">
            Calls go to <span className="mono">{savedCashfree.baseUrl}</span>. Webhooks should point
            at <span className="mono">/api/payments/cashfree/webhook</span>.
          </div>
          <div className="row gap-6">
            <button
              className="btn primary"
              disabled={saveCashfree.isPending || !cashfree}
              onClick={() => saveCashfree.mutate(cf)}
            >
              {saveCashfree.isPending ? 'Saving…' : 'Save Cashfree settings'}
            </button>
            {cashfree ? (
              <button className="btn" onClick={() => setCashfree(null)}>
                Discard changes
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
